// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import type { JSX } from "react";
import { connect } from "react-redux";
import { FormattedMessage, defineMessages, IntlContext, IntlShape } from "react-intl";
import { Button } from "react-bootstrap";

import { TimePeriod } from "types";
import { IAction, ActionType, raiseAction, IActionTypeSelectGuiItem } from "actions";
import {
  getTrendIoGraphViewMode,
  getDateString,
  getIoType,
  getSiteSelection,
  getGraphBoundaryDatesFromPeriod,
} from "guiutils";
import { IState } from "states/states";
import { IPeriodData, IStateGui } from "states/guistates";
import { GraphViewMode } from "states/cachedstates";

import TrendMemberSelect from "./TrendMemberSelect";

import SvgBarView from "icons/iconBarView";
import SvgLineView from "icons/iconGraphView";
import SvgHorizontalView from "icons/iconHorizontalBarView";
import SvgListView from "icons/iconListView";

interface ITrendHeadingExtProps {
  identifier: keyof IStateGui;
  showGraphModes?: GraphViewMode[];
  showTrendData?: boolean;
  includeDate: boolean;
  title?: string | JSX.Element;
}

interface ITrendHeadingProps {
  trendMember: "energy" | "power" | "costs";
  xZoom: TimePeriod;
  minX?: number;
  maxX?: number;
  graphStart: number | undefined;
  graphEnd: number | undefined;
  isAtEnd: boolean;
  graphViewMode: GraphViewMode;
}

const mapStateToProps = (state: IState, ownProps: ITrendHeadingExtProps) => {
  const selection = getSiteSelection(state);

  const curPeriod = state.gui[ownProps.identifier] as IPeriodData;
  const { timePeriod, graphStart, graphEnd } = getGraphBoundaryDatesFromPeriod(
    curPeriod!,
    state.site.tzOffset,
  );

  const props: ITrendHeadingProps = {
    trendMember: state.gui.trendMember,
    xZoom: timePeriod,
    minX: curPeriod.zoomMin,
    maxX: curPeriod.zoomMax,
    graphStart,
    graphEnd,
    isAtEnd: graphEnd === undefined,
    graphViewMode:
      getTrendIoGraphViewMode(
        selection,
        getIoType(state),
        Object.keys(state.trending.ioTrendFull).length > 0,
        ownProps.showTrendData ?? false,
      ) ?? GraphViewMode.BAR,
  };
  return props;
};

interface ITrendHeadingDispatch {
  onSwitchGraphView: (selection: GraphViewMode) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void) => {
  const dispatchProps: ITrendHeadingDispatch = {
    onSwitchGraphView: (selection: GraphViewMode) => {
      const payload: IActionTypeSelectGuiItem = { selection };
      dispatch(raiseAction(ActionType.ACT_SELECT_GRAPHVIEWMODE, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  switchToGraph: {
    id: "switch to graph view",
  },
  switchToList: {
    id: "switch to list view",
  },
});

class TrendHeadingComponent extends React.Component<
  ITrendHeadingProps & ITrendHeadingExtProps & ITrendHeadingDispatch
> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const dateString =
      getDateString(
        this.props.xZoom,
        this.props.graphStart,
        this.props.graphEnd,
        this.props.isAtEnd,
        this.context.formatMessage({
          id: `thisperiod_${this.props.xZoom === TimePeriod.CUSTOM ? TimePeriod.DAY : (this.props.xZoom ?? "")}`,
        }),
        this.context.locale,
      ) ?? "-";

    return (
      <div id="trendheading" className="mx-2">
        <h1>
          {this.props.title ?? <FormattedMessage id={`TrendMember_${this.props.trendMember}`} />}
          {this.props.includeDate && dateString && (
            <>
              : <span className="trendheadingdate">{dateString}</span>
            </>
          )}
        </h1>
        {this.props.showGraphModes?.length && (
          <div id="graphswitch">
            {this.props.identifier === "trendPeriod" &&
              this.props.graphViewMode !== GraphViewMode.TABLE && (
                <TrendMemberSelect className="me-2" />
              )}
            {this.props.showGraphModes.includes(GraphViewMode.BAR) === true && (
              <Button
                variant="secondary"
                className={this.props.graphViewMode === GraphViewMode.BAR ? "active" : undefined}
                title={this.context.formatMessage(messages.switchToGraph)}
                onClick={this.props.onSwitchGraphView.bind(this, GraphViewMode.BAR)}
              >
                <SvgBarView />
              </Button>
            )}
            {this.props.showGraphModes.includes(GraphViewMode.LINE) === true && (
              <>
                <Button
                  variant="secondary"
                  className={this.props.graphViewMode === GraphViewMode.LINE ? "active" : undefined}
                  title={this.context.formatMessage(messages.switchToGraph)}
                  onClick={this.props.onSwitchGraphView.bind(this, GraphViewMode.LINE)}
                >
                  <SvgLineView />
                </Button>
              </>
            )}
            {this.props.showGraphModes.includes(GraphViewMode.DIGITAL) === true && (
              <>
                <Button
                  variant="secondary"
                  className={
                    this.props.graphViewMode === GraphViewMode.DIGITAL ? "active" : undefined
                  }
                  title={this.context.formatMessage(messages.switchToGraph)}
                  onClick={this.props.onSwitchGraphView.bind(this, GraphViewMode.DIGITAL)}
                >
                  <SvgHorizontalView />
                </Button>
              </>
            )}
            {this.props.showGraphModes.includes(GraphViewMode.TABLE) === true && (
              <Button
                variant="secondary"
                className={this.props.graphViewMode === GraphViewMode.TABLE ? "active" : undefined}
                title={this.context.formatMessage(messages.switchToList)}
                onClick={this.props.onSwitchGraphView.bind(this, GraphViewMode.TABLE)}
              >
                <SvgListView />
              </Button>
            )}
          </div>
        )}
      </div>
    );
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(TrendHeadingComponent);
